import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { SortIcon } from '@/components/common/SortIcon';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { formatCurrency } from '@/lib/utils/currency';
import { BillStatusBadge } from './BillStatusBadge';
import { BillActionsCell } from './BillActionsCell';
import { formatBillType } from './BillDetailCards';
import type { Bill, BillFilters } from '../types';

interface GetColumnsParams {
  orderBy: string;
  order: 'asc' | 'desc' | '';
  onSort: (field: string) => void;
  filters: BillFilters;
  onEdit?: (bill: Bill) => void;
}

export function useBillColumns({
  orderBy,
  order,
  onSort,
  onEdit,
}: GetColumnsParams): ColumnDef<Bill>[] {
  const { t } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);

  const columns: ColumnDef<Bill>[] = [
    {
      accessorKey: 'id',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('id')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('id')}
        >
          <p>{t('id')}</p>
          <SortIcon active={orderBy === 'id'} direction={order} />
        </div>
      ),
      cell: ({ row }) => (
        <Link
          to={`/app/bills/${row.original.id}`}
          className="text-blue-700 hover:underline"
        >
          {new Date(row.original.createdAt).getFullYear()}-{row.original.id}
        </Link>
      ),
    },
    {
      accessorKey: 'total',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('total')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('total')}
        >
          <p>{t('total')}</p>
          <SortIcon active={orderBy === 'total'} direction={order} />
        </div>
      ),
      cell: ({ row }) => (
        <div>
          {formatCurrency(row.original.total, {
            locale: 'uz-UZ',
            currency: row.original.currency,
          })}
        </div>
      ),
    },
    {
      accessorKey: 'discount',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('discount')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('discount')}
        >
          <p>{t('discountPercent')}</p>
          <SortIcon active={orderBy === 'discount'} direction={order} />
        </div>
      ),
      cell: ({ row }) => <div>{row.original.discount}%</div>,
    },
    {
      accessorKey: 'paid',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('paid')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('paid')}
        >
          <p>{t('paid')}</p>
          <SortIcon active={orderBy === 'paid'} direction={order} />
        </div>
      ),
      cell: ({ row }) => (
        <div>
          {formatCurrency(row.original.paid, {
            locale: 'uz-UZ',
            currency: row.original.currency,
          })}
        </div>
      ),
    },
    {
      accessorKey: 'state',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('state')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('state')}
        >
          <p>{t('state')}</p>
          <SortIcon active={orderBy === 'state'} direction={order} />
        </div>
      ),
      cell: ({ row }) => <BillStatusBadge state={row.original.state} />,
    },
    {
      accessorKey: 'description',
      header: () => (
        <div className="flex items-center">
          <p>{t('billForm.descriptionLabel')}</p>
        </div>
      ),
      cell: ({ row }) => (
        <div className="max-w-[220px] truncate" title={row.original?.description ?? ''}>
          {row.original?.description ?? '-'}
        </div>
      ),
    },
    {
      accessorKey: 'isManual',
      header: () => (
        <div className="flex items-center">
          <p>{t('billForm.originLabel')}</p>
        </div>
      ),
      cell: ({ row }) =>
        row.original?.isManual ? (
          <Badge className="border border-blue-200 bg-blue-50 text-blue-700">
            {t('billForm.origin.manual')}
          </Badge>
        ) : (
          <Badge variant="secondary">{t('billForm.origin.auto')}</Badge>
        ),
    },
    {
      accessorKey: 'type',
      header: () => (
        <div className="flex items-center">
          <p>{t('billType')}</p>
        </div>
      ),
      cell: ({ row }) =>
        row.original.type ? (
          <Badge className="border border-violet-200 bg-violet-50 text-violet-700">
            {formatBillType(row.original.type)}
          </Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      accessorKey: 'contractName',
      header: () => (
        <div className="flex items-center">
          <p>{t('contractName')}</p>
        </div>
      ),
      cell: ({ row }) => {
        const contract = row.original.contract;
        if (!contract) return <div>-</div>;
        return (
          <Link
            to={`/app/contracts/${contract.id}`}
            className="text-blue-700 hover:underline"
          >
            {contract.name}
          </Link>
        );
      },
    },
    {
      accessorKey: 'clientName',
      header: () => (
        <div className="flex items-center">
          <p>{t('clientName')}</p>
        </div>
      ),
      cell: ({ row }) => {
        const client = row.original.contract?.client;
        if (!client) return <div>-</div>;
        return (
          <div>
            {client.lastname ?? ''} {client.name ?? ''} {client.middlename ?? ''}
          </div>
        );
      },
    },
    {
      accessorKey: 'clientPhone',
      header: () => (
        <div className="flex items-center">
          <p>{t('clientPhone')}</p>
        </div>
      ),
      cell: ({ row }) => {
        const phone = row.original.contract?.client?.phone;
        return <div>{phone ?? '-'}</div>;
      },
    },
    {
      accessorKey: 'clientType',
      header: () => (
        <div className="flex items-center">
          <p>{t('clientType')}</p>
        </div>
      ),
      cell: ({ row }) => {
        const clientType = row.original.contract?.client?.type;
        return <div>{clientType ? t(`clientTypes.${clientType}`) : '-'}</div>;
      },
    },
    {
      accessorKey: 'createdAt',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('createdAt')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('createdAt')}
        >
          <p>{t('createdAt')}</p>
          <SortIcon active={orderBy === 'createdAt'} direction={order} />
        </div>
      ),
      cell: ({ row }) => {
        const createdAt = row.original.createdAt;
        return <div>{format(new Date(createdAt), 'yyyy-MM-dd HH:mm')}</div>;
      },
    },
  ];

  // Add actions column if user has permissions
  if (hasAccess('bill', 'update') || hasAccess('bill', 'delete') || hasAccess('bill', 'download')) {
    columns.splice(0, 0, {
      id: 'actions',
      header: t('actionsLabel'),
      cell: ({ row }) => <BillActionsCell bill={row.original} onEdit={onEdit} />,
    });
  }

  return columns;
}
